import { FormControlLabel, Stack, Switch, TextField } from "@mui/material";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { useGetVirtualContractDropdownsQuery } from "src/shared/api/dropdowns/partner";
import { useGetPartnerSystemDropdownsByIdQuery } from "src/shared/api/system";
import {
  DatePickerField,
  NumberTextField,
  SelectField,
} from "src/shared/components";
import { currenciesSymbols } from "src/shared/constants/currency.constants";
import { notSelectedOption } from "src/shared/constants/selectOptions/selectOptions";
import { INameValue } from "src/shared/types/common.types";
import { ECurrency } from "src/shared/types/currency.types";
import { messages } from "src/shared/utils/hookFormRules";
import {
  IPartnerVirtualContractFormValues,
  IVirtualContractBonusRangeItem,
} from "../../../model/types";

interface IContractFormProps {
  isEdit?: boolean;
  bonusRanges?: Array<IVirtualContractBonusRangeItem>;
  currency?: ECurrency;
  KDAcurrency?: ECurrency;
  complexContractsOptions: INameValue<number>[];
  partnerId: string;
}
export function VirtualContractForm({
  isEdit,
  bonusRanges,
  complexContractsOptions,
  KDAcurrency,
  currency,
  partnerId,
}: IContractFormProps) {
  const { data } = useGetVirtualContractDropdownsQuery();

  const { data: dropdowns } = useGetPartnerSystemDropdownsByIdQuery({
    partner_id: partnerId,
  });

  const { virtual_contract_state, currency: currencyOptions } = data || {};
  const methods = useFormContext<IPartnerVirtualContractFormValues>();
  const { control } = methods;

  const is_bonus_enabled = useWatch({
    control: control,
    name: "is_bonus_enabled",
  });
  const resolvedKdaCurrencySymbol = KDAcurrency
    ? currenciesSymbols[KDAcurrency]
    : undefined;
  const resolvedServiceCurrencySymbol = currency
    ? currenciesSymbols[currency]
    : undefined;

  return (
    <Stack direction="column" spacing={4}>
      <Controller
        control={control}
        name="partner_contract_id"
        render={({ field: { ref, ...field }, fieldState: { error } }) => (
          <SelectField<number | "">
            inputRef={ref}
            fullWidth
            {...field}
            label="Договор КДА"
            error={!!error}
            helperText={error?.message}
            options={[notSelectedOption, ...complexContractsOptions]}
          />
        )}
      />
      {isEdit && (
        <Controller
          control={control}
          name="contract_number"
          render={({ field: { ref, ...field }, fieldState: { error } }) => (
            <TextField
              inputRef={ref}
              fullWidth
              {...field}
              label="№ Договора"
              error={!!error}
              helperText={error?.message}
              disabled
            />
          )}
        />
      )}
      <Controller
        name="contract_date"
        control={control}
        render={({ field: { ref, ...field }, fieldState: { error } }) => (
          <DatePickerField
            inputRef={ref}
            {...field}
            disableFuture
            error={error}
            label="Дата договора"
          />
        )}
        rules={{ required: messages.required }}
      />
      <Controller
        control={control}
        name="site"
        render={({ field: { ref, ...field }, fieldState: { error } }) => (
          <TextField
            inputRef={ref}
            fullWidth
            {...field}
            label="Сайт"
            error={!!error}
            helperText={error?.message}
          />
        )}
      />
      <Controller
        control={control}
        name="service_name"
        render={({ field: { ref, ...field }, fieldState: { error } }) => (
          <SelectField
            inputRef={ref}
            fullWidth
            {...field}
            label="Сервис"
            error={!!error}
            helperText={error?.message}
            options={dropdowns?.services}
          />
        )}
        rules={{ required: messages.required }}
      />
      <Controller
        control={control}
        name="state"
        render={({ field: { ref, ...field }, fieldState: { error } }) => (
          <SelectField
            inputRef={ref}
            fullWidth
            {...field}
            label="Статус договора"
            error={!!error}
            helperText={error?.message}
            options={virtual_contract_state}
          />
        )}
        rules={{ required: messages.required }}
      />
      <Controller
        control={control}
        name="service_currency"
        render={({ field: { ref, ...field }, fieldState: { error } }) => (
          <SelectField
            inputRef={ref}
            fullWidth
            {...field}
            label="Валюта сервиса"
            error={!!error}
            helperText={error?.message}
            options={currencyOptions}
          />
        )}
        rules={{ required: messages.required }}
      />
      <Controller
        control={control}
        name="note"
        render={({ field: { ref, ...field } }) => (
          <TextField
            inputRef={ref}
            fullWidth
            {...field}
            label="Примечание"
            multiline
            rows={3}
            slotProps={{
              htmlInput: {
                maxLength: 255,
              },
            }}
          />
        )}
      />
      <Controller
        control={control}
        name="is_bonus_enabled"
        render={({ field }) => (
          <FormControlLabel
            control={<Switch {...field} checked={!!field.value} />}
            label="Бонус"
          />
        )}
      />
      {is_bonus_enabled &&
        bonusRanges?.map((range) => (
          <Stack direction="row" spacing={2} key={range.id}>
            <NumberTextField
              name={`bonus_ranges.${range.id}.from`}
              label="Сумма(от)"
              value={range.spends_from}
              disabled
              slotProps={{
                input: {
                  endAdornment: resolvedKdaCurrencySymbol
                    ? resolvedKdaCurrencySymbol
                    : resolvedServiceCurrencySymbol,
                },
              }}
            />
            <NumberTextField
              name={`bonus_ranges.${range.id}.to`}
              label="Бонусы"
              value={range.spends_percentage}
              disabled
              slotProps={{
                input: {
                  endAdornment: "%",
                },
              }}
            />
          </Stack>
        ))}
    </Stack>
  );
}
